import { createColumnHelper } from "@tanstack/react-table";

import CrudActions from "components/crud/CrudActions";

const columnHelper =
    createColumnHelper();


/*
|--------------------------------------------------------------------------
| DATE
|--------------------------------------------------------------------------
*/

const formatDate = (
    value
) => {

    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return date.toLocaleDateString(
        "en-IN",
        {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }
    );
};


/*
|--------------------------------------------------------------------------
| CURRENCY
|--------------------------------------------------------------------------
*/

const formatCurrency = (
    value
) => {

    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return "-";
    }

    const number =
        Number(value);

    if (
        Number.isNaN(number)
    ) {
        return `₹${value}`;
    }

    return `₹${number.toLocaleString(
        "en-IN",
        {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    )}`;
};


/*
|--------------------------------------------------------------------------
| STATUS
|--------------------------------------------------------------------------
*/

const getStatusClass = (
    status
) => {

    switch (status) {

        case "draft":
            return "bg-gray-50 text-gray-700 border-gray-200";

        case "submitted":
            return "bg-blue-50 text-blue-700 border-blue-200";

        case "verified":
            return "bg-green-50 text-green-700 border-green-200";

        case "rejected":
            return "bg-red-50 text-red-700 border-red-200";

        case "cancelled":
            return "bg-orange-50 text-orange-700 border-orange-200";

        default:
            return "bg-gray-50 text-gray-700 border-gray-200";
    }
};


/*
|--------------------------------------------------------------------------
| COLUMNS
|--------------------------------------------------------------------------
*/

export const collectionColumns = ({
    onEdit,
    onCancel,
    onSubmit,
    onVerify,
    onReject,
    onView,
} = {}) => {

    /*
    |--------------------------------------------------------------------------
    | CURRENT USER
    |--------------------------------------------------------------------------
    */

    let user = {};

    try {

        user = JSON.parse(
            localStorage.getItem(
                "user"
            ) || "{}"
        );

    } catch (error) {

        console.error(
            "Unable to parse user",
            error
        );

    }


    const permissions =
        Array.isArray(
            user?.permissions
        )
            ? user.permissions
            : [];


    const roles =
        Array.isArray(
            user?.roles
        )
            ? user.roles
            : [];


    /*
    |--------------------------------------------------------------------------
    | HELPERS
    |--------------------------------------------------------------------------
    */

    const hasPermission = (
        permission
    ) =>
        permissions.includes(
            permission
        );


    const isAdmin =
        roles.includes(
            "admin"
        ) ||
        roles.includes(
            "super_admin"
        );


    const isManager =
        roles.includes(
            "manager"
        ) ||
        roles.includes(
            "team_leader"
        ) ||
        roles.includes(
            "regional_manager"
        );


    const isSalesPerson =
        roles.includes(
            "sales_person"
        );


    /*
    |--------------------------------------------------------------------------
    | SALES PERSON PERMISSIONS
    |--------------------------------------------------------------------------
    */

    const canSalesUpdate =
        isSalesPerson &&
        (
            hasPermission(
                "collections.update"
            ) ||
            hasPermission(
                "collections.manage"
            )
        );


    const canCancel =
        isSalesPerson &&
        (
            hasPermission(
                "collections.delete"
            ) ||
            hasPermission(
                "collections.manage"
            )
        );


    /*
    |--------------------------------------------------------------------------
    | REVIEW PERMISSION
    |--------------------------------------------------------------------------
    */

    const canReview =
        (
            isAdmin ||
            isManager
        ) &&
        (
            hasPermission(
                "collections.verify"
            ) ||
            hasPermission(
                "collections.manage"
            )
        );


    return [

        /*
        |--------------------------------------------------------------------------
        | DATE
        |--------------------------------------------------------------------------
        */

        columnHelper.accessor(
            "collectionDate",
            {

                id: "collectionDate",

                header: "Date",

                cell: (info) => (
                    formatDate(
                        info.getValue()
                    )
                ),

            }
        ),


        /*
        |--------------------------------------------------------------------------
        | EMPLOYEE
        |--------------------------------------------------------------------------
        */

        columnHelper.accessor(
            "employee.name",
            {

                id: "employee",

                header: "Employee",

                cell: (info) => {

                    const item =
                        info.row.original;

                    return (
                        <div>

                            <div
                                className="
                                    font-semibold
                                    text-navy-700
                                "
                            >
                                {
                                    info.getValue() ||
                                    "-"
                                }
                            </div>

                            {
                                item?.employee?.employeeCode && (

                                    <div
                                        className="
                                            text-xs
                                            text-gray-500
                                        "
                                    >
                                        {
                                            item.employee.employeeCode
                                        }
                                    </div>

                                )
                            }

                        </div>
                    );
                },

            }
        ),


        /*
        |--------------------------------------------------------------------------
        | CUSTOMER
        |--------------------------------------------------------------------------
        */

        columnHelper.display({

            id: "customer",

            header: "Customer",

            cell: ({ row }) => {

                const collection =
                    row.original;

                const customer =
                    collection.school?.name ||
                    collection.party?.name ||
                    "-";

                const code =
                    collection.school?.schoolCode ||
                    collection.party?.partyCode ||
                    "";


                return (
                    <div>

                        <div
                            className="
                                font-medium
                                text-gray-800
                            "
                        >
                            {customer}
                        </div>

                        {
                            code && (

                                <div
                                    className="
                                        text-xs
                                        text-gray-500
                                    "
                                >
                                    {code}
                                </div>

                            )
                        }

                    </div>
                );
            },

        }),


        /*
        |--------------------------------------------------------------------------
        | TYPE
        |--------------------------------------------------------------------------
        */

        columnHelper.accessor(
            "balanceType",
            {

                id: "balanceType",

                header: "Type",

                cell: (info) => (

                    <span
                        className="
                            capitalize
                            text-gray-700
                        "
                    >
                        {
                            info.getValue() ||
                            "-"
                        }
                    </span>

                ),

            }
        ),


        /*
        |--------------------------------------------------------------------------
        | AMOUNT
        |--------------------------------------------------------------------------
        */

        columnHelper.accessor(
            "amount",
            {

                id: "amount",

                header: "Amount",

                cell: (info) => (

                    <span
                        className="
                            font-semibold
                            text-gray-800
                        "
                    >
                        {
                            formatCurrency(
                                info.getValue()
                            )
                        }
                    </span>

                ),

            }
        ),


        /*
        |--------------------------------------------------------------------------
        | MODE
        |--------------------------------------------------------------------------
        */

        columnHelper.accessor(
            "mode",
            {

                id: "mode",

                header: "Mode",

                cell: (info) => {

                    const value =
                        info.getValue() ||
                        "-";

                    return (
                        <span
                            className="
                                capitalize
                            "
                        >
                            {
                                String(
                                    value
                                ).replace(
                                    /_/g,
                                    " "
                                )
                            }
                        </span>
                    );
                },

            }
        ),


        /*
        |--------------------------------------------------------------------------
        | STATUS
        |--------------------------------------------------------------------------
        */

        columnHelper.accessor(
            "status",
            {

                id: "status",

                header: "Status",

                cell: (info) => {

                    const status =
                        info.getValue();

                    return (
                        <span
                            className={`
                                inline-flex
                                rounded-full
                                border
                                px-3
                                py-1
                                text-xs
                                font-semibold
                                capitalize
                                ${getStatusClass(
                                    status
                                )}
                            `}
                        >
                            {
                                status
                                    ? String(
                                        status
                                    ).replace(
                                        /_/g,
                                        " "
                                    )
                                    : "-"
                            }
                        </span>
                    );
                },

            }
        ),


        /*
        |--------------------------------------------------------------------------
        | ACTIONS
        |--------------------------------------------------------------------------
        */

        columnHelper.display({

            id: "actions",

            header: "Actions",

            cell: ({ row }) => {

                const collection =
                    row.original;

                const status =
                    collection?.status;


                /*
                |--------------------------------------------------------------------------
                | EDIT
                |--------------------------------------------------------------------------
                */

                const canEdit =
                    canSalesUpdate &&
                    [
                        "draft",
                        "rejected",
                    ].includes(
                        status
                    );


                /*
                |--------------------------------------------------------------------------
                | SUBMIT
                |--------------------------------------------------------------------------
                */

                const canSubmit =
                    canSalesUpdate &&
                    [
                        "draft",
                        "rejected",
                    ].includes(
                        status
                    );


                /*
                |--------------------------------------------------------------------------
                | VERIFY / REJECT
                |--------------------------------------------------------------------------
                */

                const canVerifyReject =
                    canReview &&
                    status ===
                        "submitted";


                /*
                |--------------------------------------------------------------------------
                | CANCEL
                |--------------------------------------------------------------------------
                */

                const canCancelCollection =
                    canCancel &&
                    [
                        "draft",
                        "rejected",
                    ].includes(
                        status
                    );


                return (

                    <div
                        className="
                            flex
                            items-center
                            gap-2
                            whitespace-nowrap
                        "
                    >

                        <CrudActions

                            /*
                            |--------------------------------------------------------------------------
                            | VIEW
                            |--------------------------------------------------------------------------
                            */

                            showView={
                                Boolean(
                                    onView
                                )
                            }

                            onView={
                                onView
                                    ? () =>
                                        onView(
                                            collection
                                        )
                                    : undefined
                            }

                            viewTitle="View Collection"


                            /*
                            |--------------------------------------------------------------------------
                            | EDIT
                            |--------------------------------------------------------------------------
                            */

                            showEdit={
                                canEdit
                            }

                            onEdit={
                                canEdit &&
                                typeof onEdit === "function"
                                    ? () =>
                                        onEdit(
                                            collection
                                        )
                                    : undefined
                            }

                            editTitle="Edit Collection"


                            /*
                            |--------------------------------------------------------------------------
                            | APPROVE / VERIFY
                            |--------------------------------------------------------------------------
                            */

                            showApprove={
                                canVerifyReject
                            }

                            onApprove={
                                canVerifyReject &&
                                typeof onVerify === "function"
                                    ? () =>
                                        onVerify(
                                            collection.id
                                        )
                                    : undefined
                            }

                            approveTitle="Verify Collection"


                            /*
                            |--------------------------------------------------------------------------
                            | REJECT
                            |--------------------------------------------------------------------------
                            */

                            showReject={
                                canVerifyReject
                            }

                            onReject={
                                canVerifyReject &&
                                typeof onReject === "function"
                                    ? () =>
                                        onReject(
                                            collection.id
                                        )
                                    : undefined
                            }

                            rejectTitle="Reject Collection"


                            /*
                            |--------------------------------------------------------------------------
                            | DELETE / CANCEL
                            |--------------------------------------------------------------------------
                            */

                            showDelete={
                                canCancelCollection
                            }

                            onDelete={
                                canCancelCollection &&
                                typeof onCancel === "function"
                                    ? () =>
                                        onCancel(
                                            collection.id
                                        )
                                    : undefined
                            }

                            deleteTitle="Cancel Collection"

                        />


                        {/* =================================================
                            SUBMIT
                        ================================================= */}

                        {
                            canSubmit &&
                            typeof onSubmit === "function" && (

                                <button
                                    type="button"
                                    onClick={() =>
                                        onSubmit(
                                            collection.id
                                        )
                                    }
                                    title="Submit Collection"
                                    aria-label="Submit Collection"
                                    className="
                                        inline-flex
                                        h-9
                                        w-9
                                        items-center
                                        justify-center
                                        rounded-lg
                                        border
                                        border-indigo-200
                                        bg-indigo-50
                                        text-indigo-600
                                        transition-all
                                        duration-150
                                        hover:border-indigo-300
                                        hover:bg-indigo-100
                                        hover:text-indigo-700
                                        focus:outline-none
                                        focus:ring-2
                                        focus:ring-indigo-200
                                    "
                                >

                                    {/* Submit icon */}

                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        className="h-4 w-4"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="
                                                M4 12h15
                                                m0 0l-6-6
                                                m6 6l-6 6
                                            "
                                        />
                                    </svg>

                                </button>

                            )
                        }

                    </div>

                );

            },

        }),

    ];

};